import {
  Briefcase,
  BriefcaseBusiness,
  Building,
  Calendar,
  CalendarRange,
  CaseSensitive,
  Cloud,
  Coins,
  Hash,
  House,
  Link,
  ListCheck,
  ListChecks,
  Mail,
  MapPin,
  Percent,
  Phone,
  Ruler,
  Share2,
  ShieldUser,
  Signature,
  Speech,
  SquareCheck,
  Tag,
  UserCheck,
  UserRound,
  Users,
  type LucideIcon
} from 'lucide-react';
import {
  type ContactDetailType,
  type FieldKind,
  type FieldSpec,
  type FieldSpecOf,
  fieldSpecUtils
} from 'v5/core/entity-schema/entitySchema.types';
import { coreFieldKey } from 'v5/core/entity-schema/resource/fieldSpecs';
import {
  CORE_LABEL_NAMES,
  type CoreLabelName
} from 'v5/core/entity-schema/resource/resourceSchemaCopy';
import { customFieldTypeIcon } from 'v5/patterns/widgets/customFieldTypeIcons';

/**
 * What a resource's core field looks like wherever it is named: a table
 * column, a filter, a unit of the resource form, the entity card. Custom
 * fields show their type (`CustomFieldTypeIcon`).
 */
export const RESOURCE_CORE_FIELD_ICONS: Record<CoreLabelName, LucideIcon> = {
  full_name: UserRound,
  pronouns: Speech,
  emails: Mail,
  phone_numbers: Phone,
  socials: Share2,
  // Not a globe: that is the Global section and an inherited value.
  websites: Link,
  tags: Tag,
  groups: Users,
  locations: MapPin,
  rates: Coins,
  represented_by_id: UserCheck,
  organisation_id: Building,
  nick_name: Signature,
  role: Briefcase
};

/**
 * A field's icon wherever the resource form names it (a unit's title, the
 * field finder): a custom field's type, a core field's own, else its kind's.
 */
export function resourceSpecIcon(spec: FieldSpec): LucideIcon {
  const definition = fieldSpecUtils.definitionOf(spec);
  if (definition !== null) return customFieldTypeIcon(definition.data_type);
  const core = CORE_LABEL_NAMES.find(name => coreFieldKey(name) === spec.key);
  if (core !== undefined) return RESOURCE_CORE_FIELD_ICONS[core];
  return kindIcon(spec);
}

function kindIcon<K extends FieldKind>(spec: FieldSpecOf<K>): LucideIcon {
  return KIND_ICONS[spec.kind](spec);
}

/** A kind's own icon, for a field neither custom nor a named core one; a kind missing here does not compile. */
const KIND_ICONS: {
  [K in FieldKind]: (spec: FieldSpecOf<K>) => LucideIcon;
} = {
  text: () => CaseSensitive,
  boolean: () => SquareCheck,
  number: () => Hash,
  date: () => Calendar,
  'date-range': () => CalendarRange,
  'single-select': () => ListCheck,
  'multi-select': () => ListChecks,
  measurement: () => Ruler,
  currency: () => Coins,
  url: () => Link,
  percentage: () => Percent,
  height: () => Ruler,
  dimension: () => Ruler,
  'measurement-legacy': () => Ruler,
  'netsuite-master-project': () => Cloud,
  name: () => UserRound,
  pronouns: () => Speech,
  'contact-details': spec => CONTACT_DETAIL_ICON[spec.detailType],
  tags: () => Tag,
  groups: () => Users,
  location: () => MapPin,
  rate: () => Coins,
  'entity-ref': spec =>
    spec.column === 'organisation_id' ? Building : UserCheck
};

/** Contact types (`CONTACT_INFO_TYPES`) that have an icon of their own. */
const CONTACT_TYPE_ICONS: Partial<Record<string, LucideIcon>> = {
  work: BriefcaseBusiness,
  personal: House,
  // As the Agent field: the person who represents the resource.
  agent: UserCheck,
  guardian: ShieldUser
};

/**
 * A contact detail's lead by its type (work, personal…); `other`, no type
 * and the empty row show the detail's own icon (an envelope, a link).
 */
export function contactTypeIcon(
  detailType: ContactDetailType,
  type: string | null
): LucideIcon {
  return (
    (type === null ? undefined : CONTACT_TYPE_ICONS[type]) ??
    CONTACT_DETAIL_ICON[detailType]
  );
}

const CONTACT_DETAIL_ICON: { [T in ContactDetailType]: LucideIcon } = {
  EMAIL: Mail,
  PHONE: Phone,
  SOCIAL: Share2,
  WEBSITE: Link
};
